import { useMemo, useState } from "react";
import { Cpu, Layers, Clock, Users } from "lucide-react";

const scopeOptions = [
  { id: "ai", label: "AI / Automation", icon: Cpu, weight: 3 },
  { id: "app", label: "Full-Stack App", icon: Layers, weight: 2 },
  { id: "rescue", label: "Project Rescue", icon: Users, weight: 2 },
];

const timelineOptions = [
  { id: "immediate", label: "Immediate", weeks: 4, velocity: 3 },
  { id: "30-60", label: "30–60 Days", weeks: 8, velocity: 2 },
  { id: "explore", label: "Exploratory", weeks: 14, velocity: 1 },
];

const teamOptions = [
  { id: "solo", label: "No internal team", factor: 1.6 },
  { id: "small", label: "Small team (1-3)", factor: 1.2 },
  { id: "mature", label: "Mature org (4+)", factor: 1.0 },
];

export default function DiagnosticTool() {
  const [scope, setScope] = useState(["ai"]);
  const [timeline, setTimeline] = useState("30-60");
  const [team, setTeam] = useState("small");
  const [complexity, setComplexity] = useState(60);

  const result = useMemo(() => {
    const scopeWeight = scope.reduce((sum, id) => {
      const o = scopeOptions.find((s) => s.id === id);
      return sum + (o ? o.weight : 0);
    }, 0);
    const tl = timelineOptions.find((t) => t.id === timeline);
    const tm = teamOptions.find((t) => t.id === team);

    const baseWeeks = (tl?.weeks || 8) + scopeWeight * 1.5;
    const adjustedWeeks = Math.round(baseWeeks * (tm?.factor || 1) * (0.8 + complexity / 200));
    const engineers = Math.max(1, Math.ceil(scopeWeight * 0.6 + complexity / 50));
    const sprints = Math.max(2, Math.ceil(adjustedWeeks / 2));

    let track = "Bespoke Engineering";
    if (scope.includes("ai")) track = "AI Solutions Pipeline";
    if (scope.length === 1 && scope[0] === "rescue") track = "Technical Rescue & Scoping";

    return { weeks: adjustedWeeks, engineers, sprints, track };
  }, [scope, timeline, team, complexity]);

  const toggleScope = (id) => {
    setScope((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  return (
    <div className="te-terminal-window">
      {/* Header */}
      <div className="border-b border-[#1F2833] px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF3355]/80"></span>
          <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
          <span className="h-2.5 w-2.5 rounded-full bg-[#1F2833] border border-[#B91C2C]/40"></span>
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#C5C6C7]/70">
          traceelements --diagnostic --interactive
        </div>
        <div className="font-mono text-[11px] text-[#FF3355]">SCANNING</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Inputs */}
        <div className="lg:col-span-7 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-[#1F2833] space-y-10">
          {/* Scope */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
              &gt; Step 01 · Select Scope (multi-select)
            </div>
            <div className="flex flex-wrap gap-3">
              {scopeOptions.map((o) => {
                const Icon = o.icon;
                const isOn = scope.includes(o.id);
                return (
                  <button
                    key={o.id}
                    data-testid={`diag-scope-${o.id}`}
                    onClick={() => toggleScope(o.id)}
                    className={`inline-flex items-center gap-2 px-4 py-3 font-mono text-xs uppercase tracking-[0.18em] border transition-all ${
                      isOn
                        ? "bg-[#B91C2C]/15 border-[#FF3355] text-[#FF3355]"
                        : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
                    }`}
                  >
                    <span className="font-mono text-[#FF3355]">[{isOn ? "x" : " "}]</span>
                    <Icon size={14} />
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
              &gt; Step 02 · Timeline
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {timelineOptions.map((o) => {
                const isOn = timeline === o.id;
                return (
                  <button
                    key={o.id}
                    data-testid={`diag-timeline-${o.id}`}
                    onClick={() => setTimeline(o.id)}
                    className={`text-left px-4 py-4 border font-mono text-xs uppercase tracking-[0.18em] transition-all ${
                      isOn
                        ? "border-[#FF3355] text-[#FF3355] bg-[#B91C2C]/10"
                        : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
                    }`}
                  >
                    <Clock size={14} className="mb-2" />
                    <div>{o.label}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Team */}
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
              &gt; Step 03 · Internal Team Status
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {teamOptions.map((o) => {
                const isOn = team === o.id;
                return (
                  <button
                    key={o.id}
                    data-testid={`diag-team-${o.id}`}
                    onClick={() => setTeam(o.id)}
                    className={`text-left px-4 py-4 border font-mono text-xs uppercase tracking-[0.18em] transition-all ${
                      isOn
                        ? "border-[#FF3355] text-[#FF3355] bg-[#B91C2C]/10"
                        : "border-[#1F2833] text-[#C5C6C7] hover:border-[#B91C2C]"
                    }`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Complexity */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C]">
                &gt; Step 04 · Technical Complexity
              </div>
              <div className="font-mono text-xs text-[#FF3355]">{complexity}/100</div>
            </div>
            <input
              data-testid="diag-complexity-slider"
              type="range"
              min={0}
              max={100}
              value={complexity}
              onChange={(e) => setComplexity(Number(e.target.value))}
              className="w-full accent-[#FF3355]"
            />
          </div>
        </div>

        {/* Output panel */}
        <div className="lg:col-span-5 p-8 lg:p-10 bg-[#0d1117] relative te-scanline" data-testid="diag-result-panel">
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#B91C2C] mb-4">
            // diagnostic.output
          </div>
          <div className="space-y-1 font-mono text-xs text-[#C5C6C7]">
            <div>[<span className="text-[#FF3355]">SCAN</span>] inputs ingested</div>
            <div>[<span className="text-[#FF3355]">PARSE</span>] {scope.length} scope module(s)</div>
            <div>[<span className="text-[#FF3355]">CALC</span>] velocity vector resolved</div>
            <div>[<span className="text-[#FF3355]">OK</span>] estimate compiled</div>
          </div>

          <div className="mt-8 space-y-6">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">recommended track</div>
              <div className="font-display text-2xl font-bold text-[#F5F5F7] mt-1" data-testid="diag-track">
                {result.track}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-px bg-[#1F2833] border border-[#1F2833]">
              <div className="bg-[#0B0C10] p-4">
                <div className="font-display text-2xl text-[#FF3355] font-bold" data-testid="diag-weeks">{result.weeks}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7] mt-1">est. weeks</div>
              </div>
              <div className="bg-[#0B0C10] p-4">
                <div className="font-display text-2xl text-[#FF3355] font-bold" data-testid="diag-sprints">{result.sprints}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7] mt-1">sprints</div>
              </div>
              <div className="bg-[#0B0C10] p-4">
                <div className="font-display text-2xl text-[#FF3355] font-bold" data-testid="diag-engineers">{result.engineers}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#C5C6C7] mt-1">engineers</div>
              </div>
            </div>

            <a
              href="/terminal"
              data-testid="diag-cta-handoff"
              className="block text-center bg-[#B91C2C] text-[#0B0C10] px-5 py-4 font-mono text-xs uppercase tracking-[0.22em] font-bold hover:bg-[#FF3355] transition-colors"
            >
              [ Hand-off to Terminal → ]
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
